import { useEffect, useState } from "react"

import { BottomSheet } from "@/components/ui/bottom-sheet"
import { ButtonSpinner } from "@/shared/components/button-spinner"
import { cn } from "@/lib/utils"
import { formatMoney } from "@/shared/lib/money"
import type { ProductPlan } from "@/features/products/types"
import { useCheckout } from "@/features/payments"
import { PAYRAILS_FIELD_IDS, resetPayrails, validateCardFields } from "@/features/payments"
import type { PayrailsFieldState } from "@/features/payments"
import { savingsPercent, strikePrice } from "../domain/pricing"
import { SavingsBadge } from "./savings-badge"

interface CardPaymentSheetProps {
  plan: ProductPlan
  email: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

/** Bottom-sheet card payment: Payrails hosted fields + gradient "Proceed payment". */
export function CardPaymentSheet({ plan, email, open, onClose, onSuccess }: CardPaymentSheetProps) {
  // Mount the Payrails hosted fields lazily, once the sheet has finished sliding in
  // (`sheetOpened` ← BottomSheet `onOpened`): only users who actually open the card
  // sheet pay for loading the secure-field iframes (Apple Pay users never do). The
  // session is pre-warmed (`warmPayrails`), so this is just the iframe mounts.
  // Latches true on the first `onOpened` and stays (the mounted fields survive close
  // via `keepMounted` + the provider; leaving checkout unmounts the sheet →
  // `resetPayrails`).
  const [sheetOpened, setSheetOpened] = useState(false)

  const { sdkReady, sdkError, status, fieldStates, payWithCard } = useCheckout(plan, email, {
    theme: "dark",
    initCard: sheetOpened,
  })

  const savings = savingsPercent(plan)
  const strike = strikePrice(plan)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  // Latches true on the first "Proceed" tap with an incomplete form; that's the only
  // trigger for the red per-field errors (no on-blur / on-keystroke).
  const [showErrors, setShowErrors] = useState(false)

  // Tear down the Payrails form only when the sheet leaves the tree (e.g. navigating off
  // checkout), so a fresh visit re-mounts into a fresh node. It survives open/close.
  useEffect(() => {
    return () => resetPayrails()
  }, [])

  const handlePay = async () => {
    const first = firstName.trim()
    const last = lastName.trim()
    // Validate every field locally BEFORE tokenize. Empty/invalid → per-field
    // "Fill in the field" hints, no toast, no tokenize (which for an empty form would
    // surface Payrails' raw "Error collecting data from the iframe!"). Only a genuine
    // post-submit failure (card declined / backend) still raises the top toast.
    const card = validateCardFields()
    const allValid = first !== "" && last !== "" && card.cardNumber && card.expiry && card.cvc
    if (!allValid) {
      setShowErrors(true)
      return
    }
    const ok = await payWithCard({ firstName: first, lastName: last })
    if (ok) onSuccess()
  }

  // Close via the X / backdrop / Escape: clear the validation state so a reopen starts
  // clean. Entered values survive (keepMounted keeps the names in React state and the
  // card in the Payrails iframe) — only the "attempted submit" flag is reset.
  const handleClose = () => {
    setShowErrors(false)
    onClose()
  }

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      title="Pay with card"
      keepMounted
      onOpened={() => setSheetOpened(true)}
    >
      {/* Total due today */}
      <div className="flex items-center gap-4 bg-purple-light/10 px-4 py-3">
        <span className="text-base font-semibold text-purple-light">Total due today:</span>
        <span className="h-px flex-1 bg-purple-light/20" />
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1.5">
            {strike != null && (
              <span className="text-base text-purple-light/40 line-through decoration-peach">
                {formatMoney(strike, plan.currency)}
              </span>
            )}
            <span className="text-base font-semibold text-white">
              {formatMoney(plan.price, plan.currency)}
            </span>
          </div>
          {savings > 0 && <SavingsBadge percent={savings} />}
        </div>
      </div>

      <div className="flex flex-col gap-6 p-4 pb-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <NameField
              label="First name"
              value={firstName}
              onChange={setFirstName}
              submitted={showErrors}
            />
            <NameField
              label="Last name"
              value={lastName}
              onChange={setLastName}
              submitted={showErrors}
            />
          </div>

          {/* Payrails secure fields — one small iframe per field (COLLECT), mounted into
              the slots below. The Expires+CVC row is OUR flexbox in the main document and
              every slot reserves its box up front (label + h-12), so nothing depends on a
              cross-origin iframe laying out a multi-field row — the intermittent row
              collapse that plagued the single composable iframe can't happen. Each slot
              keeps an opaque cover until every field reports READY (`sdkReady`), hiding
              the iframes' white bootstrap paint. */}
          {sdkError ? (
            <p className="py-8 text-center text-sm text-peach">{sdkError}</p>
          ) : (
            <div className="flex flex-col gap-4">
              <SecureFieldSlot
                label="Card number"
                id={PAYRAILS_FIELD_IDS.cardNumber}
                ready={sdkReady}
                state={fieldStates.cardNumber}
                submitted={showErrors}
              />
              <div className="flex gap-3">
                <SecureFieldSlot
                  label="Expires on"
                  id={PAYRAILS_FIELD_IDS.expiry}
                  ready={sdkReady}
                  state={fieldStates.expiry}
                  submitted={showErrors}
                />
                <SecureFieldSlot
                  label="CVC"
                  id={PAYRAILS_FIELD_IDS.cvc}
                  ready={sdkReady}
                  state={fieldStates.cvc}
                  submitted={showErrors}
                />
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handlePay}
          disabled={!sdkReady || status === "processing"}
          className={cn(
            "flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent text-base font-semibold text-white transition-opacity hover:opacity-90",
            // The button stays tappable with an empty form on purpose: a tap runs local
            // validation and reveals the per-field hints (Figma: Card_Payment_Errors).
            // Dim only while the fields are still loading; during processing it stays
            // bright and shows the spinner (Figma: Card_Payment_Processing).
            status !== "processing" && !sdkReady && "opacity-50",
          )}
        >
          {status === "processing" ? <ButtonSpinner /> : "Proceed payment"}
        </button>
      </div>
    </BottomSheet>
  )
}

interface SecureFieldSlotProps {
  label: string
  id: string
  ready: boolean
  state?: PayrailsFieldState
  submitted: boolean
}

/** Slot for one Payrails secure-field iframe: our label + bordered box + cover. */
function SecureFieldSlot({ label, id, ready, state, submitted }: SecureFieldSlotProps) {
  // Errors are revealed only after a submit attempt (never while typing or on blur) —
  // then they clear reactively as the field is fixed. Empty → red border + "Fill in the
  // field"; non-empty but not yet complete → red border only (no misleading "Fill in the
  // field"). Focus keeps visual priority over the red border.
  const isEmpty = state?.empty ?? true
  const emptyError = submitted && isEmpty
  const error = emptyError || (submitted && !isEmpty && !state?.complete)
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-sm text-purple-light/70">{label}</span>
      {/* The border + rounded corners are OURS (same tokens as the First/Last name
          inputs), so the corners stay crisp — a border drawn inside the secure iframe
          would be clipped here by the rounded overflow. `overflow-hidden` still clamps
          the borderless iframe to the input height (the SDK reserves a white error-text
          area below it). Focus/invalid come from the field's own events (`state`). */}
      <div
        className={cn(
          "relative h-12 overflow-hidden rounded-xl border transition-colors",
          state?.focused
            ? "border-main-purple"
            : error
              ? "border-peach"
              : "border-purple-light/20",
        )}
      >
        <div id={id} data-slot="payrails-field" />
        <div
          className={cn(
            "pointer-events-none absolute inset-0 bg-dark-surface transition-opacity duration-300",
            ready ? "opacity-0" : "opacity-100",
          )}
        >
          <div className="h-12 w-full animate-pulse rounded-xl bg-purple-light/10" />
        </div>
      </div>
      {emptyError && <FieldError />}
    </div>
  )
}

interface NameFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  submitted: boolean
}

function NameField({ label, value, onChange, submitted }: NameFieldProps) {
  // A name has no "invalid" state — only empty vs filled. The empty error is revealed on
  // submit and clears as soon as the field has text.
  const error = submitted && value.trim() === ""
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-sm text-purple-light/70">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        className={cn(
          "h-12 w-full min-w-0 rounded-xl border px-3 text-base font-medium text-purple-light outline-none transition-colors placeholder:text-purple-light/30 focus:border-main-purple",
          error ? "border-peach" : "border-purple-light/20",
        )}
      />
      {error && <FieldError />}
    </label>
  )
}

/** Inline "Fill in the field" hint under an empty/invalid field (Card_Payment_Errors). */
function FieldError() {
  return <p className="text-xs text-peach">Fill in the field</p>
}
